/**
 * Public home page for visitors who are not signed in. Everything described here is a
 * feature the app actually has; keep it that way when editing the copy (no invented customer
 * counts, ratings or testimonials). The product previews are illustrations: labels, statuses
 * and unlabelled bars only, no client names or figures.
 *
 * The page keeps its own warm palette (ivory, deep navy, muted gold) in light and dark mode
 * alike, so it uses fixed colors rather than the app's theme tokens.
 */
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  CalendarCheck,
  Calculator,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Clock3,
  FileSearch,
  FileSignature,
  FileSpreadsheet,
  Fingerprint,
  KeyRound,
  LineChart,
  Lock,
  Megaphone,
  Menu,
  MessageSquare,
  Receipt,
  ScrollText,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Timer,
  Upload,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

const CONTACT = "mailto:angoupou@cnafinancialservices.com?subject=ClientFlow%20%E2%80%94%20a%20walkthrough%20for%20our%20firm";

// The landing page's own palette.
const C = {
  ivory: "bg-[#FBF7EF]",
  sand: "bg-[#F3ECDF]",
  navy: "bg-[#0E1B30]",
  ink: "text-[#0E1B30]",
  body: "text-[#4A5468]",
  gold: "text-[#876614]",
  line: "border-[#E6DCCB]",
};

const btn = "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8913E] focus-visible:ring-offset-2";
const primaryBtn = `${btn} bg-[#0E1B30] px-7 py-3.5 text-white shadow-[0_10px_30px_-10px_rgba(14,27,48,0.6)] hover:-translate-y-0.5 hover:bg-[#16284a]`;
const goldBtn = `${btn} bg-[#D9B868] px-7 py-3.5 text-[#0E1B30] shadow-[0_10px_30px_-12px_rgba(217,184,104,0.8)] hover:-translate-y-0.5 hover:bg-[#E4C67E]`;
const ghostBtn = `${btn} border border-[#0E1B30]/15 bg-white/70 px-7 py-3.5 text-[#0E1B30] hover:border-[#0E1B30]/30 hover:bg-white`;

export function LandingPage() {
  return (
    <div className={`min-h-screen overflow-x-hidden ${C.ivory} ${C.ink} antialiased [color-scheme:light]`}>
      <Header />
      <main>
        <Hero />
        <Connects />
        <OnePlatform />
        <PlatformTour />
        <AiSection />
        <ClientExperience />
        <OwnerView />
        <Growth />
        <Security />
        <Onboarding />
        <Faq />
        <ClosingCta />
      </main>
      <Footer />
    </div>
  );
}

function Logo({ light }: { light?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <img src="/logo.png" alt="" className="h-8 w-8 rounded-md" />
      <span role="img" aria-label="ClientFlow" className={`font-display text-xl font-semibold tracking-wide ${light ? "text-white" : "text-[#0E1B30]"}`}>
        <span aria-hidden="true">Client</span>
        <span aria-hidden="true" className={light ? "text-[#C9A24E]" : "text-[#876614]"}>
          Flow
        </span>
      </span>
    </span>
  );
}

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const links: Array<[string, string]> = [
    ["#platform", "Platform"],
    ["#ai", "AI"],
    ["#clients", "Client portal"],
    ["#analytics", "Analytics"],
    ["#security", "Security"],
    ["#faq", "FAQ"],
  ];
  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled || open ? "border-b border-[#E6DCCB] bg-[#FBF7EF]/90 backdrop-blur-md" : "border-b border-transparent"}`}>
      <div className="mx-auto flex h-[4.25rem] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <a href="/" aria-label="ClientFlow home">
          <Logo />
        </a>
        <nav aria-label="Main" className="hidden items-center gap-7 text-[15px] text-[#4A5468] lg:flex">
          {links.map(([href, label]) => (
            <a key={href} href={href} className="transition-colors hover:text-[#0E1B30]">
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-1 sm:gap-2">
          <a href="/sign-in" className="hidden rounded-full px-4 py-2 text-sm font-medium text-[#0E1B30]/80 hover:text-[#0E1B30] sm:inline-flex">
            Sign in
          </a>
          <span className="hidden sm:block">
            <a href={CONTACT} className={`${btn} bg-[#0E1B30] px-5 py-2.5 text-sm text-white hover:bg-[#16284a]`}>
              Book a walkthrough
            </a>
          </span>
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls="landing-menu" aria-label="Menu" className="ml-1 rounded-full p-2 text-[#0E1B30] hover:bg-[#0E1B30]/5 lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="landing-menu" aria-label="Sections" className="border-t border-[#E6DCCB] px-4 pb-4 lg:hidden">
          <ul className="grid grid-cols-2 gap-1 pt-2">
            {[...links, ["/sign-in", "Sign in"] as [string, string], [CONTACT, "Book a walkthrough"] as [string, string]].map(([href, label]) => (
              <li key={href}>
                <a href={href} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 text-[15px] text-[#0E1B30] hover:bg-[#F3ECDF]">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}

// --- Hero ------------------------------------------------------------------------------------------

function Hero() {
  return (
    <section className="relative isolate overflow-hidden">
      {/* Warm light and a fine grid */}
      <div aria-hidden className="absolute -top-40 left-1/2 -z-10 h-[38rem] w-[70rem] max-w-[200vw] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(217,184,104,0.32),transparent)]" />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60 [background-image:linear-gradient(rgba(14,27,48,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,48,0.05)_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(ellipse_at_top,black_25%,transparent_70%)]"
      />
      <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 md:pt-20 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#D9B868]/50 bg-white/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0E1B30]/75 shadow-sm sm:text-xs">
            <Sparkles className="h-3.5 w-3.5 text-[#876614]" aria-hidden />
            The practice platform for accounting firms
          </span>
          <h1 className="font-display mx-auto mt-8 text-[2.7rem] font-medium leading-[1.04] tracking-[-0.02em] text-[#0E1B30] sm:text-6xl md:text-[4.6rem]">
            Run your whole firm
            <br className="hidden sm:block" /> <span className="italic text-[#876614]">beautifully</span>, in one place.
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-[#4A5468] md:text-xl">
            Tax, accounting, payroll and CFO advisory, with a client portal your clients will actually enjoy, AI that reads documents for your team to check, and the numbers that tell you how your firm is doing.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a href={CONTACT} className={`${primaryBtn} group w-full text-base sm:w-auto`}>
              Book a walkthrough
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </a>
            <a href="#platform" className={`${ghostBtn} w-full text-base sm:w-auto`}>
              Take the tour
            </a>
          </div>
          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#4A5468]">
            {["We set up your firm with you", "Bring your clients from a spreadsheet or OLT Pro", "Two-step sign-in for every staff member"].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#876614]" aria-hidden />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <HeroComposition />
      </div>
    </section>
  );
}

/** The workspace, with the client portal and the owner's figures floating beside it. Labels only. */
function HeroComposition() {
  const nav: Array<{ icon: LucideIcon; label: string; active?: boolean }> = [
    { icon: Clock3, label: "Dashboard", active: true },
    { icon: Users, label: "Clients" },
    { icon: FileSearch, label: "Document Review" },
    { icon: Calculator, label: "Tax" },
    { icon: BookOpen, label: "Accounting" },
    { icon: Wallet, label: "Payroll" },
    { icon: LineChart, label: "CFO & Advisory" },
    { icon: BarChart3, label: "Firm Analytics" },
  ];
  const rows: Array<{ label: string; tag: string; state: "done" | "ready" | "waiting" | "ai" }> = [
    { label: "W-2 read and matched to its request", tag: "Tax", state: "ai" },
    { label: "Return ready for client signature", tag: "Tax", state: "ready" },
    { label: "Month-end close checks passed", tag: "Accounting", state: "done" },
    { label: "Client answered transaction questions", tag: "Accounting", state: "done" },
    { label: "CFO report ready for review", tag: "Advisory", state: "ready" },
    { label: "Waiting on brokerage statement", tag: "Tax", state: "waiting" },
  ];
  const tone = {
    done: ["Done", "bg-emerald-50 text-emerald-700"],
    ready: ["Ready", "bg-[#0E1B30]/[0.06] text-[#0E1B30]"],
    waiting: ["Waiting", "bg-amber-50 text-amber-700"],
    ai: ["Read by AI", "bg-[#D9B868]/20 text-[#7A5C1C]"],
  } as const;
  return (
    <div aria-hidden className="relative mx-auto mt-16 max-w-6xl md:mt-20">
      <div className="absolute -inset-x-10 -bottom-10 top-16 -z-10 rounded-[3rem] bg-[#0E1B30]/10 blur-3xl" />
      {/* The workspace */}
      <div className="overflow-hidden rounded-[1.4rem] border border-[#E6DCCB] bg-white shadow-[0_40px_80px_-30px_rgba(14,27,48,0.45)]">
        <div className="flex items-center gap-2 border-b border-[#EFE7D9] bg-[#FAF6EE] px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-[#E8DCC6]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#E8DCC6]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#E8DCC6]" />
          <span className="ml-3 hidden rounded-full bg-white px-3 py-0.5 text-[11px] text-[#6B7280] sm:inline">clientflow · Dashboard</span>
        </div>
        <div className="grid md:grid-cols-[220px_1fr]">
          <aside className="hidden flex-col gap-0.5 bg-[#0E1B30] p-4 md:flex">
            <div className="mb-5 px-2">
              <Logo light />
            </div>
            {nav.map(({ icon: Icon, label, active }) => (
              <span key={label} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] ${active ? "bg-white/10 text-white" : "text-white/60"}`}>
                <Icon className={`h-4 w-4 ${active ? "text-[#D9B868]" : ""}`} />
                {label}
              </span>
            ))}
          </aside>
          <div className="min-w-0 p-5 sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">Good morning</p>
            <p className="font-display text-2xl text-[#0E1B30] sm:text-3xl">Today at the firm</p>
            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { label: "Ready for review", icon: CheckCircle2, w: "w-2/3" },
                { label: "Waiting on clients", icon: CircleDot, w: "w-1/2" },
                { label: "Due this week", icon: Bell, w: "w-3/4" },
                { label: "Read by AI today", icon: Sparkles, w: "w-2/5" },
              ].map(({ label, icon: Icon, w }) => (
                <div key={label} className="rounded-xl border border-[#EFE7D9] bg-[#FDFBF7] p-3.5">
                  <Icon className="h-4 w-4 text-[#876614]" />
                  <p className="mt-2 text-xs text-[#6B7383]">{label}</p>
                  <div className={`mt-2.5 h-1.5 ${w} rounded-full bg-[#0E1B30]/10`} />
                </div>
              ))}
            </div>
            <ul className="mt-6 divide-y divide-[#F1EADD] rounded-xl border border-[#EFE7D9]">
              {rows.map((t) => (
                <li key={t.label} className="flex items-center gap-3 px-4 py-3 text-[13px] text-[#0E1B30] sm:text-sm">
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${t.state === "done" ? "bg-emerald-600 text-white" : "border border-[#DCCFB8]"}`}>{t.state === "done" && <Check className="h-3 w-3" />}</span>
                  <span className="min-w-0 flex-1 truncate">{t.label}</span>
                  <span className="hidden text-xs text-[#6B7280] sm:inline">{t.tag}</span>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${tone[t.state][1]}`}>{tone[t.state][0]}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* The client's phone */}
      <div className="absolute -bottom-16 -left-6 hidden w-[220px] rotate-[-4deg] rounded-[2rem] border-[8px] border-[#0E1B30] bg-[#FBF7EF] p-3.5 shadow-[0_30px_60px_-20px_rgba(14,27,48,0.55)] lg:block xl:-left-14">
        <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-[#0E1B30]/15" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">Your portal</p>
        <p className="font-display text-lg text-[#0E1B30]">2 things need you</p>
        <ul className="mt-3 grid gap-1.5">
          {[
            [Upload, "Upload your 1099s"],
            [FileSignature, "Sign Form 8879"],
            [Receipt, "Pay your invoice"],
          ].map(([Icon, text]) => {
            const I = Icon as LucideIcon;
            return (
              <li key={text as string} className="flex items-center gap-2 rounded-lg border border-[#EFE7D9] bg-white px-2.5 py-2 text-[11px] text-[#0E1B30]">
                <I className="h-3.5 w-3.5 text-[#876614]" />
                {text as string}
              </li>
            );
          })}
        </ul>
      </div>

      {/* The owner's figures */}
      <div className="absolute -right-6 -top-10 hidden w-[250px] rotate-[3deg] rounded-2xl border border-[#E6DCCB] bg-white p-4 shadow-[0_30px_60px_-20px_rgba(14,27,48,0.45)] lg:block xl:-right-12">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">
          <BarChart3 className="h-3.5 w-3.5 text-[#876614]" /> Firm Analytics
        </p>
        <p className="mt-1 font-display text-base text-[#0E1B30]">Billed by month</p>
        <div className="mt-3 flex h-20 items-end gap-1.5">
          {[38, 44, 41, 92, 70, 46, 48, 52, 50, 55, 58, 62].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-sm bg-[#0E1B30]/80" style={{ height: `${h}%`, opacity: i === 11 ? 1 : 0.55 + i * 0.03 }} />
          ))}
        </div>
        <p className="mt-2 flex items-center gap-1 text-[11px] text-emerald-700">
          <LineChart className="h-3 w-3" /> Compared with the same months last year
        </p>
      </div>
    </div>
  );
}

// --- Connects with ----------------------------------------------------------------------------------

function Connects() {
  const items = ["QuickBooks Online", "Stripe", "Google Calendar & Meet", "Claude by Anthropic", "OLT Pro", "Twilio SMS"];
  return (
    <section aria-label="Connects with" className="border-y border-[#E6DCCB] bg-[#F7F1E6]">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-5 px-4 py-10 sm:px-6 md:flex-row md:justify-between lg:px-8">
        <p className="shrink-0 text-xs font-semibold uppercase tracking-[0.2em] text-[#6F5C3C]">Works with the tools you use</p>
        <ul className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 md:justify-end">
          {items.map((t) => (
            <li key={t} className="font-display text-lg text-[#0E1B30]/70">
              {t}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// --- One platform ----------------------------------------------------------------------------------

function Eyebrow({ children, light }: { children: ReactNode; light?: boolean }) {
  return <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${light ? "text-[#D9B868]" : "text-[#876614]"}`}>{children}</p>;
}
function Title({ children, light, center }: { children: ReactNode; light?: boolean; center?: boolean }) {
  return <h2 className={`font-display mt-4 text-[2.1rem] font-medium leading-[1.1] tracking-[-0.015em] sm:text-5xl ${light ? "text-white" : "text-[#0E1B30]"} ${center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}`}>{children}</h2>;
}
function Lead({ children, light, center }: { children: ReactNode; light?: boolean; center?: boolean }) {
  return <p className={`mt-5 text-lg leading-relaxed ${light ? "text-white/70" : "text-[#4A5468]"} ${center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}`}>{children}</p>;
}

function OnePlatform() {
  const replaces: Array<{ icon: LucideIcon; title: string; text: string }> = [
    { icon: Users, title: "Practice management", text: "Every engagement, task, deadline and reviewer on one board per service." },
    { icon: Smartphone, title: "Client portal", text: "Documents, organizers, messages, approvals and payments, on any device." },
    { icon: FileSignature, title: "E-signatures", text: "Engagement letters and Form 8879, with identity checks and a consent record." },
    { icon: Receipt, title: "Billing & payments", text: "Invoices, online card and bank payments, and documents held until paid." },
    { icon: CalendarCheck, title: "Scheduling", text: "Booking pages with Google Calendar and Meet links, plus text reminders." },
    { icon: BarChart3, title: "Firm reporting", text: "Revenue, collections, workload and growth, compared with last year." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
      <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
        <div>
          <Eyebrow>One platform</Eyebrow>
          <Title>
            Retire the patchwork. <span className="italic text-[#876614]">Keep the craft.</span>
          </Title>
        </div>
        <Lead>
          Most firms stitch together a portal, an e-signature service, a scheduler, a payments tool and a spreadsheet of deadlines. ClientFlow is all of it, built around one client record, so nothing is copied twice and nothing falls between the cracks.
        </Lead>
      </div>
      <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-[#E6DCCB] bg-[#E6DCCB] sm:grid-cols-2 lg:grid-cols-3">
        {replaces.map(({ icon: Icon, title, text }) => (
          <div key={title} className="group bg-[#FBF7EF] p-8 transition-colors duration-300 hover:bg-white">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0E1B30] text-[#D9B868] transition-transform duration-300 group-hover:-translate-y-0.5">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <h3 className="mt-6 font-display text-2xl text-[#0E1B30]">{title}</h3>
            <p className="mt-2 leading-relaxed text-[#4A5468]">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// --- Platform tour -------------------------------------------------------------------------------

type Practice = { key: string; icon: LucideIcon; label: string; headline: string; text: string; points: string[]; preview: ReactNode };

function PlatformTour() {
  const practices: Practice[] = [
    {
      key: "tax",
      icon: Calculator,
      label: "Tax",
      headline: "Every return, from organizer to signature.",
      text: "Organizers that adapt to each client, documents read and filed for you, a reviewer's sign-off before anything goes out, and a clean package for your tax software.",
      points: [
        "Organizers and intake forms by return type, with answers that route themselves",
        "Each return's preparer, reviewer, due dates and extensions on one board",
        "A tax input sheet in Form 1040 order, exported for OLT Pro's import tool",
        "Estimated payments, IRS and state notices, and a refund tracker clients can check",
      ],
      preview: <TaxPreview />,
    },
    {
      key: "accounting",
      icon: Briefcase,
      label: "Accounting",
      headline: "Clean books every month, connected to QuickBooks.",
      text: "Uncategorized transactions become plain questions clients answer from a link, and each month's close runs its checks before the period is locked.",
      points: [
        "Transaction questions answered from a link, written back to QuickBooks",
        "Monthly close with automated checks, sign-off and a period lock",
        "Reconciliation workpapers, adjusting entries and bills to pay",
        "Financial statements and a monthly report package ready to send",
      ],
      preview: <ClosePreview />,
    },
    {
      key: "payroll",
      icon: Wallet,
      label: "Payroll",
      headline: "Payroll work on the same board as everything else.",
      text: "Pay schedules, run reviews, changes and new hires, and payroll filings with their deadlines, beside the client's tax and accounting work.",
      points: ["Pay schedules and run reviews for each client", "Changes and new hires collected from the client portal", "Quarterly and annual filings on the deadlines calendar"],
      preview: <PayrollPreview />,
    },
    {
      key: "cfo",
      icon: LineChart,
      label: "CFO & Advisory",
      headline: "Advice built on closed books, not guesses.",
      text: "Dashboards, budgets and forecasts drawn from months whose books are closed, with reports your team reviews before any client sees them.",
      points: [
        "Performance dashboards and KPI scorecards with alerts",
        "Budgets, 12-month driver-based forecasts and 13-week cash",
        "Scenarios, benchmarks and meeting agendas from the dashboard",
        "Tax planning with projections and opportunities, shared with clients",
      ],
      preview: <CfoPreview />,
    },
  ];
  const [active, setActive] = useState(practices[0]!.key);
  const p = practices.find((x) => x.key === active)!;
  return (
    <section id="platform" className={`scroll-mt-20 ${C.sand}`}>
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
        <div className="text-center">
          <Eyebrow>The platform</Eyebrow>
          <Title center>
            Four practices. <span className="italic text-[#876614]">One client record.</span>
          </Title>
          <Lead center>Each service has its own workspace for your team, and they share one inbox, one document vault and one portal for the client.</Lead>
        </div>
        <div role="tablist" aria-label="Practices" className="mx-auto mt-12 flex max-w-full gap-1 overflow-x-auto rounded-full border border-[#E1D5BF] bg-white/70 p-1.5 [scrollbar-width:none] sm:w-fit">
          {practices.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              id={`tab-${key}`}
              aria-selected={key === active}
              aria-controls={`panel-${key}`}
              onClick={() => setActive(key)}
              className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 ${key === active ? "bg-[#0E1B30] text-white shadow-md" : "text-[#4A5468] hover:text-[#0E1B30]"}`}
            >
              <Icon className={`h-4 w-4 ${key === active ? "text-[#D9B868]" : ""}`} aria-hidden />
              {label}
            </button>
          ))}
        </div>
        <div id={`panel-${p.key}`} role="tabpanel" aria-labelledby={`tab-${p.key}`} className="mt-12 grid items-center gap-12 lg:grid-cols-[1fr_1.05fr]">
          <div>
            <h3 className="font-display text-3xl leading-tight text-[#0E1B30] sm:text-4xl">{p.headline}</h3>
            <p className="mt-4 text-lg leading-relaxed text-[#4A5468]">{p.text}</p>
            <ul className="mt-7 grid gap-3.5">
              {p.points.map((t) => (
                <li key={t} className="flex gap-3 text-[#0E1B30]/85">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0E1B30] text-[#D9B868]">
                    <Check className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div aria-hidden className="relative">
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-[#D9B868]/20 blur-2xl" />
            {p.preview}
          </div>
        </div>
      </div>
    </section>
  );
}

function Panel({ title, children, icon: Icon }: { title: string; children: ReactNode; icon: LucideIcon }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-[#E6DCCB] bg-white shadow-[0_30px_60px_-30px_rgba(14,27,48,0.4)]">
      <div className="flex items-center gap-2 border-b border-[#F1EADD] px-6 py-4">
        <Icon className="h-4 w-4 text-[#876614]" />
        <p className="font-display text-lg text-[#0E1B30]">{title}</p>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Stages({ names, at }: { names: string[]; at: number }) {
  return (
    <ol className="flex flex-wrap gap-1.5">
      {names.map((n, i) => (
        <li key={n} className={`rounded-full px-3 py-1 text-xs ${i < at ? "bg-[#0E1B30]/[0.06] text-[#0E1B30]" : i === at ? "bg-[#0E1B30] text-white" : "border border-[#E6DCCB] text-[#6B7280]"}`}>
          {i < at && <Check className="mr-1 inline h-3 w-3" />}
          {n}
        </li>
      ))}
    </ol>
  );
}

function TaxPreview() {
  return (
    <Panel title="Individual return · 1040" icon={Calculator}>
      <Stages names={["Engagement", "Documents", "Preparation", "Review", "Signature", "E-filed"]} at={3} />
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-[#EFE7D9] p-4">
          <p className="text-xs text-[#6B7280]">Reviewer sign-off</p>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-[#0E1B30]">
            <BadgeCheck className="h-4 w-4 text-emerald-600" /> Required before signature
          </p>
        </div>
        <div className="rounded-2xl border border-[#EFE7D9] p-4">
          <p className="text-xs text-[#6B7280]">Tax input sheet</p>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-[#0E1B30]">
            <FileSpreadsheet className="h-4 w-4 text-[#876614]" /> Package for OLT Pro
          </p>
        </div>
      </div>
      <ul className="mt-5 grid gap-2 text-sm">
        {["W-2 · confirmed", "1099-INT · confirmed", "Brokerage statement · requested"].map((t, i) => (
          <li key={t} className="flex items-center gap-2.5 rounded-xl bg-[#FBF7EF] px-3.5 py-2.5 text-[#0E1B30]">
            {i < 2 ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <CircleDot className="h-4 w-4 text-amber-600" />}
            {t}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ClosePreview() {
  return (
    <Panel title="Month-end close" icon={BookOpen}>
      <ul className="grid gap-2.5 text-sm">
        {["Bank and card accounts reconciled", "No uncategorized transactions", "Payroll matches the payroll reports", "Balance sheet accounts tie out", "Manager sign-off"].map((t, i) => (
          <li key={t} className="flex items-center gap-3 rounded-xl border border-[#EFE7D9] px-4 py-3 text-[#0E1B30]">
            <span className={`flex h-5 w-5 items-center justify-center rounded-full ${i < 4 ? "bg-emerald-600 text-white" : "border border-[#DCCFB8]"}`}>{i < 4 && <Check className="h-3 w-3" />}</span>
            <span className="flex-1">{t}</span>
            {i === 4 && <span className="rounded-full bg-[#0E1B30] px-2.5 py-0.5 text-[11px] text-white">Ready</span>}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center gap-2 rounded-xl bg-[#0E1B30] px-4 py-3 text-sm text-white">
        <Lock className="h-4 w-4 text-[#D9B868]" /> The period locks when the close is signed off
      </div>
    </Panel>
  );
}

function PayrollPreview() {
  return (
    <Panel title="Payroll overview" icon={Wallet}>
      <div className="grid gap-3 sm:grid-cols-3">
        {["Runs to review", "Changes & new hires", "Filings due"].map((t, i) => (
          <div key={t} className="rounded-2xl border border-[#EFE7D9] p-4">
            <p className="text-xs text-[#6B7280]">{t}</p>
            <div className={`mt-3 h-1.5 rounded-full bg-[#0E1B30]/15 ${["w-3/4", "w-1/2", "w-2/3"][i]}`} />
          </div>
        ))}
      </div>
      <ul className="mt-5 grid gap-2 text-sm">
        {["Semi-monthly run · review", "New hire · paperwork received", "Quarterly 941 · on the calendar"].map((t) => (
          <li key={t} className="flex items-center gap-2.5 rounded-xl bg-[#FBF7EF] px-3.5 py-2.5 text-[#0E1B30]">
            <CalendarCheck className="h-4 w-4 text-[#876614]" /> {t}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function CfoPreview() {
  return (
    <Panel title="Performance dashboard" icon={LineChart}>
      <div className="grid grid-cols-3 gap-3">
        {["Revenue", "Gross margin", "Cash runway"].map((t) => (
          <div key={t} className="rounded-2xl border border-[#EFE7D9] p-3">
            <p className="text-[11px] text-[#6B7280]">{t}</p>
            <svg viewBox="0 0 100 30" className="mt-2 h-8 w-full">
              <polyline points="0,24 15,20 30,22 45,14 60,16 75,9 100,6" fill="none" stroke="#0E1B30" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl border border-[#EFE7D9] p-4">
        <p className="text-xs text-[#6B7280]">Actual · Budget · Prior year</p>
        <div className="mt-3 flex h-24 items-end gap-2">
          {[52, 60, 48, 70, 66, 78, 74, 86].map((h, i) => (
            <div key={i} className="flex flex-1 items-end gap-0.5">
              <div className="flex-1 rounded-t bg-[#0E1B30]" style={{ height: `${h}%` }} />
              <div className="flex-1 rounded-t bg-[#D9B868]" style={{ height: `${h - 8}%` }} />
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

// --- AI ----------------------------------------------------------------------------------------------

function AiSection() {
  const items: Array<{ icon: LucideIcon; title: string; text: string }> = [
    { icon: FileSearch, title: "Reads every upload", text: "W-2s, 1099s, 1098s and K-1s are read, matched to the client's request and filed in the right year's folder. Blurry photos are caught and the client is asked to retake them." },
    { icon: BadgeCheck, title: "You confirm every figure", text: "Nothing is entered on its own: your team checks what was read, side by side with the document, before it reaches the tax input sheet." },
    { icon: Sparkles, title: "Drafts, never decides", text: "Draft replies, prep briefs, summaries and suggested categories wait in AI Review for a person to approve, edit or reject." },
    { icon: Fingerprint, title: "Consent comes first", text: "AI only touches the data of clients who have signed your AI consent, and every use is recorded in an activity log." },
  ];
  return (
    <section id="ai" className={`relative scroll-mt-20 overflow-hidden ${C.navy} text-white`}>
      <div aria-hidden className="absolute -right-40 top-0 h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(closest-side,rgba(217,184,104,0.18),transparent)]" />
      <div aria-hidden className="absolute -bottom-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(closest-side,rgba(57,135,229,0.15),transparent)]" />
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <Eyebrow light>AI with judgment</Eyebrow>
            <Title light>
              The reading done for you. <span className="italic text-[#D9B868]">The decisions left to you.</span>
            </Title>
            <Lead light>ClientFlow's AI, built on Claude by Anthropic, handles the reading, matching and first drafts. Your team keeps the professional judgment, and the record of who approved what.</Lead>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {items.map(({ icon: Icon, title, text }) => (
                <div key={title}>
                  <Icon className="h-5 w-5 text-[#D9B868]" aria-hidden />
                  <h3 className="mt-3 font-semibold text-white">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/65">{text}</p>
                </div>
              ))}
            </div>
          </div>
          <div aria-hidden className="relative">
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <p className="font-display text-xl text-white">Document Review</p>
                <span className="rounded-full bg-[#D9B868]/20 px-3 py-1 text-xs text-[#E8CF92]">Read by AI · check and confirm</span>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-2xl bg-white p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">Form W-2</p>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="mt-2.5 flex gap-2">
                      <div className="h-2 w-1/3 rounded bg-[#0E1B30]/15" />
                      <div className={`h-2 rounded ${i === 1 ? "w-1/3 bg-[#D9B868]" : "w-1/2 bg-[#0E1B30]/10"}`} />
                    </div>
                  ))}
                  <div className="mt-4 h-10 rounded-lg border border-dashed border-[#0E1B30]/15" />
                </div>
                <ul className="grid gap-2 text-sm">
                  {["Wages, tips, other compensation", "Federal income tax withheld", "Social Security wages", "State wages"].map((t, i) => (
                    <li key={t} className={`flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 ${i === 0 ? "border-[#D9B868]/60 bg-[#D9B868]/10" : "border-white/10"}`}>
                      <span className="text-white/80">{t}</span>
                      <span className="h-2 w-14 rounded-full bg-white/25" />
                    </li>
                  ))}
                  <li className="mt-1 flex gap-2">
                    <span className="flex-1 rounded-full bg-[#D9B868] py-2 text-center text-xs font-semibold text-[#0E1B30]">Confirm</span>
                    <span className="flex-1 rounded-full border border-white/20 py-2 text-center text-xs text-white/80">Ask to retake</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Clients ---------------------------------------------------------------------------------------

function ClientExperience() {
  const items = [
    { icon: Upload, text: "Upload documents, or snap a photo from their phone" },
    { icon: ScrollText, text: "Fill in organizers that only ask what applies to them" },
    { icon: FileSignature, text: "Review and sign forms, including Form 8879" },
    { icon: Receipt, text: "Pay invoices online by card or bank" },
    { icon: CalendarCheck, text: "Book meetings with your team, with reminders by text" },
    { icon: MessageSquare, text: "Message their accountant in one secure thread" },
  ];
  return (
    <section id="clients" className="scroll-mt-20">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 py-24 sm:px-6 md:py-32 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        <div>
          <Eyebrow>For your clients</Eyebrow>
          <Title>
            A portal that feels <span className="italic text-[#876614]">like your firm.</span>
          </Title>
          <Lead>Your clients see what you need from them, what's done and what's next, in plain language, on their phone or their computer. Fewer emails, fewer calls asking where things stand.</Lead>
          <ul className="mt-9 grid gap-4 sm:grid-cols-2">
            {items.map(({ icon: Icon, text }) => (
              <li key={text} className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E6DCCB] bg-white text-[#876614]">
                  <Icon className="h-[18px] w-[18px]" aria-hidden />
                </span>
                <span className="pt-2 text-[15px] leading-snug text-[#0E1B30]/85">{text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-[#4A5468]">
            Already a client of a ClientFlow firm?{" "}
            <a href="/sign-in" className="font-medium text-[#0E1B30] underline decoration-[#D9B868] decoration-2 underline-offset-4 hover:decoration-[#B8913E]">
              Sign in to your portal
            </a>
            . Your firm's invitation or a sign-in code by email gets you in.
          </p>
        </div>
        <div aria-hidden className="relative mx-auto w-full max-w-[330px]">
          <div className="absolute -inset-10 -z-10 rounded-[4rem] bg-[radial-gradient(closest-side,rgba(217,184,104,0.35),transparent)]" />
          <div className="rounded-[2.8rem] border-[11px] border-[#0E1B30] bg-[#FBF7EF] p-5 shadow-[0_40px_80px_-30px_rgba(14,27,48,0.6)]">
            <div className="mx-auto mb-5 h-1.5 w-16 rounded-full bg-[#0E1B30]/15" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6B7280]">Welcome back</p>
            <p className="font-display text-2xl text-[#0E1B30]">Your tax return</p>
            <div className="mt-4 rounded-2xl bg-white p-4">
              <div className="flex gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full ${i < 2 ? "bg-[#0E1B30]" : "bg-[#0E1B30]/10"}`} />
                ))}
              </div>
              <p className="mt-2 text-xs text-[#4A5468]">In preparation · we'll message you when it's ready to sign</p>
            </div>
            <p className="mt-5 text-xs font-semibold text-[#0E1B30]">Needs you</p>
            <ul className="mt-2 grid gap-2">
              {[
                [Upload, "Upload your brokerage statement", "To do"],
                [FileSignature, "Sign your engagement letter", "To do"],
                [CheckCircle2, "Organizer submitted", "Done"],
              ].map(([Icon, text, s]) => {
                const I = Icon as LucideIcon;
                return (
                  <li key={text as string} className="flex items-center gap-2.5 rounded-xl border border-[#EFE7D9] bg-white px-3 py-2.5">
                    <I className="h-4 w-4 shrink-0 text-[#876614]" />
                    <span className="min-w-0 flex-1 text-xs text-[#0E1B30]">{text as string}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${s === "Done" ? "bg-emerald-50 text-emerald-700" : "bg-[#0E1B30]/[0.06] text-[#0E1B30]"}`}>{s as string}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 rounded-2xl bg-[#0E1B30] p-4 text-white">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Refund status</p>
              <div className="mt-2.5 flex gap-1.5">
                {["Filed", "Accepted", "Approved", "Sent"].map((t, i) => (
                  <div key={t} className="flex-1">
                    <div className={`h-1.5 rounded-full ${i < 2 ? "bg-[#D9B868]" : "bg-white/20"}`} />
                    <p className="mt-1 text-[9px] text-white/70">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- The owner's view ------------------------------------------------------------------------------

function OwnerView() {
  const stats: Array<{ icon: LucideIcon; title: string; text: string }> = [
    { icon: BarChart3, title: "Revenue and recurring revenue", text: "By month, service, client and return type, beside the same period last year." },
    { icon: Receipt, title: "Collections", text: "What clients owe by age, how fast they pay, and finished work that was never billed." },
    { icon: Timer, title: "Staff and capacity", text: "Workload, on-time work, review turnaround and an optional time tracker, with no single score." },
    { icon: Users, title: "Clients and growth", text: "Clients gained, kept and at risk, who could use another service, and which lead sources pay off." },
  ];
  return (
    <section id="analytics" className={`scroll-mt-20 ${C.sand}`}>
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 py-24 sm:px-6 md:py-32 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div aria-hidden className="order-2 lg:order-1">
          <div className="overflow-hidden rounded-3xl border border-[#E6DCCB] bg-white shadow-[0_40px_80px_-35px_rgba(14,27,48,0.45)]">
            <div className="flex flex-wrap items-center gap-2 border-b border-[#F1EADD] px-6 py-4">
              {["Overview", "Revenue", "Billing & Collections", "Staff", "Clients", "Growth"].map((t, i) => (
                <span key={t} className={`rounded-full px-3 py-1 text-xs ${i === 0 ? "bg-[#0E1B30] text-white" : "text-[#6B7383]"}`}>
                  {t}
                </span>
              ))}
            </div>
            <div className="p-6">
              <div className="rounded-2xl border border-[#EFE7D9] bg-[#FDFBF7] p-4">
                <p className="flex items-center gap-2 text-sm font-medium text-[#0E1B30]">
                  <Sparkles className="h-4 w-4 text-[#876614]" /> Needs your attention
                </p>
                <ul className="mt-3 grid gap-2 text-sm text-[#4A5468]">
                  {["Invoices more than 90 days late", "Finished returns not yet invoiced", "One preparer's week is much heavier than the rest"].map((t, i) => (
                    <li key={t} className="flex items-center gap-2.5">
                      <span className={`h-2 w-2 rounded-full ${i === 0 ? "bg-red-500" : "bg-amber-500"}`} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {["Billed", "Collected", "Clients owe you", "Recurring / month"].map((t, i) => (
                  <div key={t} className="rounded-2xl border border-[#EFE7D9] p-3">
                    <p className="text-[11px] text-[#6B7280]">{t}</p>
                    <div className="mt-2 h-3 w-3/4 rounded bg-[#0E1B30]/80" />
                    <p className={`mt-2 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${i === 2 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{i === 2 ? "watch" : "vs last year"}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-2xl border border-[#EFE7D9] p-4">
                <div className="flex h-28 items-end gap-2">
                  {[40, 46, 43, 95, 72, 48, 50, 55, 52, 58, 61, 66].map((h, i) => (
                    <div key={i} className="relative flex-1">
                      <div className="rounded-t bg-[#0E1B30]" style={{ height: `${h * 1.05}px` }} />
                      <div className="absolute inset-x-0 border-t-2 border-dashed border-[#D9B868]" style={{ bottom: `${(h - 7) * 1.05}px` }} />
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-[#6B7280]">This year · the same month last year</p>
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <Eyebrow>Firm Analytics</Eyebrow>
          <Title>
            Know how your firm is doing, <span className="italic text-[#876614]">not just how busy it is.</span>
          </Title>
          <Lead>For owners and admins: six pages of figures drawn from the work itself, compared with the same dates last year, so tax season is measured against tax season.</Lead>
          <ul className="mt-9 grid gap-6">
            {stats.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#0E1B30] text-[#D9B868]">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <h3 className="font-semibold text-[#0E1B30]">{title}</h3>
                  <p className="mt-1 leading-relaxed text-[#4A5468]">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// --- Growth --------------------------------------------------------------------------------------

function Growth() {
  const items: Array<{ icon: LucideIcon; title: string; text: string }> = [
    { icon: Megaphone, title: "Leads from every channel", text: "An inquiry form for your website and social links, each tagged by source, in one pipeline with follow-up dates." },
    { icon: CalendarCheck, title: "Booking pages", text: "Discovery calls and consultations booked online, free or paid, straight into Google Calendar with a Meet link." },
    { icon: Bell, title: "Reminders that do the chasing", text: "Document requests, organizers, estimated payments and meetings remind clients by email, and by text when they opt in." },
    { icon: ScrollText, title: "Onboarding that runs itself", text: "Intake forms by service and return type, engagement letters to sign, and every new client's first steps tracked." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
      <div className="text-center">
        <Eyebrow>Grow the firm</Eyebrow>
        <Title center>
          From first inquiry <span className="italic text-[#876614]">to loyal client.</span>
        </Title>
      </div>
      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {items.map(({ icon: Icon, title, text }, i) => (
          <div key={title} className="relative rounded-3xl border border-[#E6DCCB] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(14,27,48,0.35)]">
            <span className="font-display absolute right-6 top-5 text-4xl text-[#0E1B30]/[0.07]" aria-hidden>
              0{i + 1}
            </span>
            <Icon className="h-6 w-6 text-[#876614]" aria-hidden />
            <h3 className="mt-5 font-display text-xl text-[#0E1B30]">{title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[#4A5468]">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// --- Security ------------------------------------------------------------------------------------

function Security() {
  const items: Array<{ icon: LucideIcon; title: string; text: string }> = [
    { icon: KeyRound, title: "Two-step verification", text: "Required for every staff member, and available to every client." },
    { icon: ShieldCheck, title: "Walls between firms", text: "Each firm's data is separated by the database itself, not only by the app." },
    { icon: Lock, title: "Protected numbers", text: "Social Security, EIN and bank numbers are masked; showing them in full is recorded. Secrets are encrypted at rest." },
    { icon: ScrollText, title: "A complete audit trail", text: "Sign-ins, approvals, payments, document access and AI use are all logged." },
    { icon: Fingerprint, title: "Consent on record", text: "Section 7216 consents and AI consent are signed, dated and kept with the client." },
    { icon: BadgeCheck, title: "Review before release", text: "Returns need a reviewer's sign-off, and reports a team member's approval, before a client sees them." },
  ];
  return (
    <section id="security" className={`scroll-mt-20 ${C.navy} text-white`}>
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <Eyebrow light>Security</Eyebrow>
            <Title light>
              Built for financial data, <span className="italic text-[#D9B868]">from the database up.</span>
            </Title>
          </div>
          <Lead light>Tax documents, bank-connected books and payroll records deserve more than a password and a promise.</Lead>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-[#0E1B30] p-7 transition-colors hover:bg-[#13243f]">
              <Icon className="h-6 w-6 text-[#D9B868]" aria-hidden />
              <h3 className="mt-4 font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Onboarding ----------------------------------------------------------------------------------

function Onboarding() {
  const steps = [
    { title: "We set up your firm", text: "Your services, workflows, organizers, engagement letters and booking pages, configured with you." },
    { title: "Bring your clients", text: "Import your client list from a spreadsheet or from OLT Pro, and connect QuickBooks for accounting clients." },
    { title: "Invite your team", text: "Owners, admins, staff and read-only seats, each limited to the services they work on." },
    { title: "Invite your clients", text: "Clients get a portal invitation and can sign in with a code by email or with Google." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
      <div className="text-center">
        <Eyebrow>Getting started</Eyebrow>
        <Title center>
          Up and running <span className="italic text-[#876614]">with us beside you.</span>
        </Title>
      </div>
      <ol className="relative mt-16 grid gap-10 md:grid-cols-4 md:gap-6">
        <div aria-hidden className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-[#D9B868] to-transparent md:block" />
        {steps.map((s, i) => (
          <li key={s.title} className="relative text-center">
            <span className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#D9B868] bg-[#FBF7EF] font-display text-lg text-[#0E1B30]">{i + 1}</span>
            <h3 className="mt-5 font-display text-xl text-[#0E1B30]">{s.title}</h3>
            <p className="mx-auto mt-2 max-w-xs text-[15px] leading-relaxed text-[#4A5468]">{s.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// --- FAQ ---------------------------------------------------------------------------------------------

function Faq() {
  const qa: Array<[string, string]> = [
    ["Which services does ClientFlow cover?", "Tax preparation and planning, monthly accounting and bookkeeping, payroll, and CFO and advisory work. Each has its own workspace, and they share one client record and one client portal."],
    ["Does it work with QuickBooks and our tax software?", "Accounting clients connect QuickBooks Online: transactions, reports and answers flow both ways. For tax, the tax input sheet exports a package for OLT Pro's W-2/1099 import tool, with a worksheet, a spreadsheet and every source document named by form."],
    ["How does the AI handle client data?", "AI features run only for clients who have signed your AI consent. The AI reads and drafts; a person on your team confirms every figure and approves every message or change, and each use is logged."],
    ["Can clients pay online?", "Yes. Invoices can be paid by card or bank online through Stripe, and you can record checks, cash, Zelle and refund-transfer payments. Finished documents can be held until an invoice is paid."],
    ["What do clients need to sign in?", "An email address. They can sign in with a one-time code or with Google, and turn on two-step verification. Everything works on a phone."],
    ["How do we move over?", "We set up your firm with you, import your clients from a spreadsheet or OLT Pro, and walk your team through it. Book a walkthrough and we'll start with how your firm works today."],
  ];
  return (
    <section id="faq" className={`scroll-mt-20 ${C.sand}`}>
      <div className="mx-auto max-w-4xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
        <div className="text-center">
          <Eyebrow>Questions</Eyebrow>
          <Title center>Good to know</Title>
        </div>
        <div className="mt-14 divide-y divide-[#E1D5BF] rounded-3xl border border-[#E1D5BF] bg-white/80">
          {qa.map(([q, a]) => (
            <details key={q} className="group px-6 py-5 sm:px-8 [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-left font-display text-lg text-[#0E1B30] sm:text-xl">
                {q}
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E1D5BF] transition-transform duration-200 group-open:rotate-180">
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </span>
              </summary>
              <p className="mt-3 pr-10 leading-relaxed text-[#4A5468]">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Closing ------------------------------------------------------------------------------------

function ClosingCta() {
  return (
    <section className="px-4 py-24 sm:px-6 md:py-28 lg:px-8">
      <div className={`relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] ${C.navy} px-6 py-16 text-center text-white sm:px-12 md:py-24`}>
        <div aria-hidden className="absolute -top-32 left-1/2 h-80 w-[50rem] max-w-[200%] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(217,184,104,0.3),transparent)]" />
        <svg aria-hidden viewBox="0 0 1000 300" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full fill-none opacity-30">
          <path d="M -50 220 C 200 220, 300 80, 560 160 S 820 290, 1050 190" stroke="#D9B868" strokeWidth="1" />
          <path d="M -50 250 C 180 250, 280 120, 540 200 S 800 310, 1050 240" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />
        </svg>
        <div className="relative">
          <Eyebrow light>Your firm, elevated</Eyebrow>
          <h2 className="font-display mx-auto mt-5 max-w-3xl text-[2.2rem] font-medium leading-[1.08] sm:text-5xl md:text-6xl">
            Less chasing. <span className="italic text-[#D9B868]">More advising.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-white/70">Tell us how your firm works today. We'll show you ClientFlow with your services in mind, and set it up with you.</p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a href={CONTACT} className={`${goldBtn} group w-full text-base sm:w-auto`}>
              Book a walkthrough <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </a>
            <a href="/sign-in" className={`${btn} w-full border border-white/25 px-7 py-3.5 text-base text-white hover:bg-white/10 sm:w-auto`}>
              Sign in
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#E6DCCB]">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr] lg:px-8">
        <div>
          <Logo />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#4A5468]">The practice platform for accounting firms: tax, accounting, payroll and advisory, with a client portal, AI and firm analytics. By C&amp;A Financial Services.</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6F5C3C]">Platform</p>
          <ul className="mt-4 grid gap-2.5 text-sm text-[#4A5468]">
            {[
              ["#platform", "Practices"],
              ["#ai", "AI"],
              ["#clients", "Client portal"],
              ["#analytics", "Firm Analytics"],
              ["#security", "Security"],
            ].map(([h, l]) => (
              <li key={h}>
                <a href={h} className="hover:text-[#0E1B30]">
                  {l}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6F5C3C]">Get in touch</p>
          <ul className="mt-4 grid gap-2.5 text-sm text-[#4A5468]">
            <li>
              <a href={CONTACT} className="hover:text-[#0E1B30]">
                Book a walkthrough
              </a>
            </li>
            <li>
              <a href="/sign-in" className="hover:text-[#0E1B30]">
                Sign in
              </a>
            </li>
            <li>
              <a href="#faq" className="hover:text-[#0E1B30]">
                Questions
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E6DCCB]">
        <p className="mx-auto max-w-7xl px-4 py-6 text-xs text-[#6B7280] sm:px-6 lg:px-8">
          © {new Date().getFullYear()} C&amp;A Financial Services. QuickBooks, Stripe, Google, Claude, OLT Pro and Twilio are trademarks of their owners.
        </p>
      </div>
    </footer>
  );
}
